import FontBody2 from '../../../../../../components/fonts/font-body2/font-body2';
import { FONT_COLOR, FONT_VARIANT } from '../../../../../../components/fonts/font-props';
import IconChevronDown from '../../../../../../components/icons/icon-chevron-down/icon-chevron-down';

import FeatureCard from './feature-card';

interface IFeatureQuota {
    limit: string;
    price_per_excess_usage: number;
}

interface IFeature {
    code: number;
    name: string;
    internal_name: string;
    description: string;
    has_quota: boolean;
    is_usage_based: boolean;
    is_beta: boolean;
    is_activatable?: boolean;
    quota?: IFeatureQuota;
    knowledge_base_url?: string;
}

interface IAvailableFeature {
    id: number;
    name: string;
    description: string;
    is_active: boolean;
    source: string | null;
    is_beta: boolean;
    has_quota?: boolean;
    is_activatable?: boolean;
    knowledge_base_url?: string;
}

interface IFeaturesSectionProps {
    title: string;
    features: (IFeature | IAvailableFeature)[];
    isExpanded: boolean;
    onToggle: () => void;
    getFeatureSource?: (feature: IFeature | IAvailableFeature) => string;
    getFeatureClassName?: (feature: IFeature | IAvailableFeature) => string;
    editingQuota: { [key: number]: boolean };
    quotaValues: { [key: number]: string };
    onStartEditing: (featureId: number, currentQuota?: string) => void;
    onCancelEditing: (featureId: number) => void;
    onApplyQuota: (featureId: number) => void;
    onQuotaChange: (featureId: number, value: any) => void;
    onSetQuota?: (featureId: number, quota: number) => void;
    onActivateFeature?: (featureId: number) => void;
    onDeactivateFeature?: (featureId: number) => void;
    isActivating: boolean;
    activatingFeatureId: number | null;
    isDeactivating: boolean;
    deactivatingFeatureId: number | null;
    isSettingQuota: boolean;
    settingQuotaFeatureId: number | null;
}

const FeaturesSection: React.FC<IFeaturesSectionProps> = ({
    title,
    features,
    isExpanded,
    onToggle,
    getFeatureSource,
    getFeatureClassName,
    editingQuota,
    quotaValues,
    onStartEditing,
    onCancelEditing,
    onApplyQuota,
    onQuotaChange,
    onSetQuota,
    onActivateFeature,
    onDeactivateFeature,
    isActivating,
    activatingFeatureId,
    isDeactivating,
    deactivatingFeatureId,
    isSettingQuota,
    settingQuotaFeatureId
}) => {
    return (
        <div className="features-section">
            <div 
                className="section-header" 
                onClick={onToggle}
            >
                <FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY1}>
                    {title} ({features.length})
                </FontBody2>
                <span className={`toggle-icon ${isExpanded ? 'expanded' : 'collapsed'}`}>
                    <IconChevronDown color={`var(--color-primary-1)`} />
                </span>
            </div>
            {features.length > 0 && (
                <div className={`features-content ${isExpanded ? 'expanded' : 'collapsed'}`}>
                    <div className="features-grid">
                        {features.map(feature => {
                            const featureId = 'code' in feature ? feature.code : feature.id;
                            const featureSource = getFeatureSource ? getFeatureSource(feature) : undefined;
                            const className = getFeatureClassName ? getFeatureClassName(feature) : '';
                            
                            return (
                                <FeatureCard
                                    key={featureId}
                                    feature={feature}
                                    featureSource={featureSource}
                                    isEditing={editingQuota[featureId] || false}
                                    quotaValue={quotaValues[featureId] || '0'}
                                    onStartEditing={onStartEditing}
                                    onCancelEditing={onCancelEditing}
                                    onApplyQuota={onApplyQuota}
                                    onQuotaChange={onQuotaChange}
                                    onSetQuota={onSetQuota}
                                    onActivateFeature={onActivateFeature}
                                    onDeactivateFeature={onDeactivateFeature}
                                    isActivating={isActivating}
                                    activatingFeatureId={activatingFeatureId}
                                    isDeactivating={isDeactivating}
                                    deactivatingFeatureId={deactivatingFeatureId}
                                    isSettingQuota={isSettingQuota}
                                    settingQuotaFeatureId={settingQuotaFeatureId}
                                    className={className}
                                />
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FeaturesSection;
